import React, { useState, useRef } from 'react';
import { Send, X, AlertCircle, Loader2, Monitor, Wifi, Printer, Smartphone, Package, Paperclip, Upload, Calendar } from 'lucide-react';

const BAGIANS = ['Kasir', 'IT', 'Keuangan', 'HRD', 'Operasional', 'Marketing', 'Produksi', 'Umum'];
const DEVICE_TYPES = [
  { value: 'Komputer', icon: Monitor },
  { value: 'Printer', icon: Printer },
  { value: 'Jaringan', icon: Wifi },
  { value: 'Software', icon: Smartphone },
  { value: 'Telepon', icon: Smartphone },
  { value: 'Lainnya', icon: Package },
];
const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];

// Helper: format date to YYYY-MM-DD for input type="date"
const toDateValue = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const TicketForm = ({ onSubmit, initialData = null, onCancel }) => {
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    description: initialData?.description || '',
    bagian: initialData?.bagian || 'Umum',
    deviceType: initialData?.deviceType || 'Lainnya',
    priority: initialData?.priority || 'Medium',
    tanggal: toDateValue(initialData?.tanggal) || toDateValue(new Date()),
  });
  const [lampiranFile, setLampiranFile] = useState(null);
  const [lampiranName, setLampiranName] = useState(initialData?.lampiran || '');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLampiranFile(file);
      setLampiranName(file.name);
    }
  };

  const handleRemoveFile = () => {
    setLampiranFile(null);
    setLampiranName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.title.trim()) {
      setFormError('Title is required');
      return;
    }
    if (!formData.description.trim()) {
      setFormError('Description is required');
      return;
    }

    setSubmitting(true);
    try {
      const submitData = { ...formData };
      if (lampiranFile) {
        submitData.lampiran = lampiranFile;
      }
      await onSubmit(submitData);
      if (!initialData) {
        setFormData({ title: '', description: '', bagian: 'Umum', deviceType: 'Lainnya', priority: 'Medium', tanggal: toDateValue(new Date()) });
        setLampiranFile(null);
        setLampiranName('');
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          {initialData ? 'Edit Ticket' : 'New Ticket'}
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          {initialData
            ? 'Update your ticket details'
            : 'Report an issue or request assistance'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
        {formError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{formError}</p>
          </div>
        )}

        {/* Title */}
        <div className="card">
          <div className="card-body space-y-5">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1.5">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="input-field"
                placeholder="e.g. Kasir 1 komputer tidak bisa booting"
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1.5">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                required
                className="input-field resize-none"
                placeholder="Describe the issue in detail. What happened? When did it start? Any error messages?"
              />
            </div>
          </div>
        </div>

        {/* Tanggal */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <Calendar className="w-5 h-5 text-green-600" />
              <h3 className="section-title">Tanggal Input</h3>
            </div>
          </div>
          <div className="card-body">
            <div>
              <label htmlFor="tanggal" className="block text-sm font-medium text-gray-700 mb-1.5">
                Tanggal <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                id="tanggal"
                name="tanggal"
                value={formData.tanggal}
                onChange={handleChange}
                required
                className="input-field"
              />
              <p className="mt-1 text-xs text-gray-400">Default: hari ini. Bisa di-backdate untuk input tanggal sebelumnya.</p>
            </div>
          </div>
        </div>

        {/* Attachment */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <Paperclip className="w-5 h-5 text-orange-600" />
              <h3 className="section-title">Attachment</h3>
            </div>
          </div>
          <div className="card-body">
            <div className="flex items-center gap-3">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx,.doc,.docx"
                className="hidden"
                id="ticket-lampiran-input"
              />
              <label
                htmlFor="ticket-lampiran-input"
                className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all text-sm font-medium text-gray-600 hover:text-blue-600"
              >
                <Upload className="w-4 h-4" />
                <span>Choose File</span>
              </label>
              {lampiranName ? (
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <Paperclip className="w-4 h-4 text-blue-500" />
                  <span className="text-sm text-blue-700 truncate max-w-[200px]">{lampiranName}</span>
                  <button type="button" onClick={handleRemoveFile} className="text-red-400 hover:text-red-600 ml-1">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : initialData?.lampiran ? (
                <a
                  href={`http://localhost:5000${initialData.lampiran}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition-all"
                >
                  <Paperclip className="w-4 h-4" />
                  <span className="truncate max-w-[200px]">View current file</span>
                </a>
              ) : null}
              <span className="text-xs text-gray-400">PDF, JPG, PNG, Excel, Word (max 10MB)</span>
            </div>
          </div>
        </div>

        {/* Classification */}
        <div className="card">
          <div className="card-header">
            <h3 className="section-title">Classification</h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {/* Bagian */}
              <div>
                <label htmlFor="bagian" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Department
                </label>
                <select
                  id="bagian"
                  name="bagian"
                  value={formData.bagian}
                  onChange={handleChange}
                  className="select-field"
                >
                  {BAGIANS.map(b => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>

              {/* Device Type */}
              <div>
                <label htmlFor="deviceType" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Device Type
                </label>
                <div className="grid grid-cols-3 gap-1.5">
                  {DEVICE_TYPES.map(dt => {
                    const Icon = dt.icon;
                    const isSelected = formData.deviceType === dt.value;
                    return (
                      <button
                        key={dt.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, deviceType: dt.value }))}
                        className={`flex flex-col items-center p-2 rounded-lg border transition-all ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <Icon className={`w-4 h-4 mb-0.5 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} />
                        <span className={`text-[10px] font-medium ${isSelected ? 'text-blue-700' : 'text-gray-500'}`}>
                          {dt.value}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Priority */}
              <div>
                <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Priority
                </label>
                <div className="space-y-1.5">
                  {PRIORITIES.map(p => {
                    const colorMap = {
                      'Low': 'border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100',
                      'Medium': 'border-yellow-300 text-yellow-700 bg-yellow-50 hover:bg-yellow-100',
                      'High': 'border-orange-300 text-orange-700 bg-orange-50 hover:bg-orange-100',
                      'Urgent': 'border-red-300 text-red-700 bg-red-50 hover:bg-red-100',
                    };
                    const isSelected = formData.priority === p;
                    return (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, priority: p }))}
                        className={`w-full text-left px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all ${
                          isSelected
                            ? colorMap[p]
                            : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {p}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          {onCancel && (
            <button type="button" onClick={onCancel} className="btn-secondary">
              <X className="w-4 h-4" />
              <span>Cancel</span>
            </button>
          )}
          <button type="submit" disabled={submitting} className="btn-primary">
            {submitting ? (
              <><Loader2 className="w-4 h-4 animate-spin" /><span>Submitting...</span></>
            ) : (
              <><Send className="w-4 h-4" /><span>{initialData ? 'Update Ticket' : 'Submit Ticket'}</span></>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TicketForm;
